import React, { useCallback, useState } from "react";
import classNames from "classnames";
import { Controller, useForm, useFormState } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  BackButton,
  Button,
  Grid,
  GridCell,
  Infobox,
  Input,
  Tag,
  TagGroup,
} from "@flixbus/honeycomb-react";
import {
  Icon,
  IconCheckmark,
  IconClose,
  IconEditSolid,
  IconInfoSolid,
} from "@flixbus/honeycomb-icons-react";
import type { CalculationInfoBarProps } from "./CalculationInfoBar.types";
import CalculationLifecycleControls from "./CalculationLifecycleControls.component";
import CalculationLifecycleErrors from "./CalculationLifecycleErrors.component";
import {
  calculationHeaderSchema,
  type CalculationHeaderFormData,
} from "./CalculationInfoBar.constants";
import { formatDate, formatLastEditedAt } from "@/utils/formatDate";
import styles from "./CalculationInfoBar.module.scss";

const calculationNameInputSize = 34;
const kamNameInputSize = 24;

const CalculationInfoBar: React.FC<CalculationInfoBarProps> = ({
  name,
  kamName,
  line,
  startDate,
  endDate,
  updatedAt,
  status,
  hasIncompleteResults,
  activeSubcategoryHasCosts,
  incompleteReason,
  inFlight,
  completeError,
  recalculateError,
  isSaving,
  onBack,
  onAddCost,
  onMarkComplete,
  onRecalculate,
  onReopen,
  onSaveDetails,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [shouldRestoreEditFocus, setShouldRestoreEditFocus] = useState(false);
  const { control, handleSubmit, reset } = useForm<CalculationHeaderFormData>({
    resolver: yupResolver(calculationHeaderSchema),
    defaultValues: { name, kamName },
    mode: "onTouched",
  });
  const { errors } = useFormState({ control });

  const closeEdit = useCallback(() => {
    setShouldRestoreEditFocus(true);
    setIsEditing(false);
  }, []);

  const enterEdit = useCallback(() => {
    reset({ name, kamName });
    setSubmitError(null);
    setShouldRestoreEditFocus(false);
    setIsEditing(true);
  }, [kamName, name, reset]);

  const cancelEdit = useCallback(() => {
    reset({ name, kamName });
    setSubmitError(null);
    closeEdit();
  }, [closeEdit, kamName, name, reset]);

  const saveDetails = useCallback(
    async (data: CalculationHeaderFormData) => {
      setSubmitError(null);
      if (data.name === name && data.kamName === kamName) {
        closeEdit();
        return;
      }
      const result = await onSaveDetails({
        name: data.name,
        kamName: data.kamName,
      });
      if (result === "success") closeEdit();
      else if (result === "error") {
        setSubmitError("Could not save changes. Try again.");
      }
    },
    [closeEdit, kamName, name, onSaveDetails],
  );

  const submit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      void handleSubmit(saveDetails)(event);
    },
    [handleSubmit, saveDetails],
  );

  return (
    <div className={styles.infoBar}>
      <Grid
        align={isEditing ? "start" : "center"}
        extraClasses={styles.headerGrid}
      >
        <GridCell
          colSpan={{ default: 12, md: 8 }}
          extraClasses={styles.leftColumn}
        >
          <div
            className={classNames(styles.leftSection, {
              [styles.leftSectionEditing]: isEditing,
            })}
          >
            <BackButton
              onClick={onBack}
              aria-label="Back to calculation list"
            />
            {isEditing ? (
              <form
                className={classNames(styles.headerEditForm, {
                  [styles.headerEditFormWithFieldError]:
                    errors.name || errors.kamName,
                })}
                onSubmit={submit}
              >
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="edit-calc-name"
                      aria-label="Calculation name"
                      extraClasses={styles.nameEditInput}
                      maxLength={255}
                      size={calculationNameInputSize}
                      autoFocus
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      onBlur={field.onBlur}
                      innerRef={field.ref}
                      valid={errors.name ? false : undefined}
                      infoError={errors.name?.message}
                    />
                  )}
                />
                <Controller
                  name="kamName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="edit-kam-name"
                      aria-label="KAM name"
                      inlineLabelLeft="KAM:"
                      extraClasses={styles.kamEditInput}
                      maxLength={255}
                      size={kamNameInputSize}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      onBlur={field.onBlur}
                      innerRef={field.ref}
                      valid={errors.kamName ? false : undefined}
                      infoError={errors.kamName?.message}
                    />
                  )}
                />
                <TagGroup>
                  <div className={styles.tags}>
                    <Tag display="outlined" small>
                      {line}
                    </Tag>
                    <Tag display="outlined" small>
                      {`${formatDate(startDate)} - ${formatDate(endDate)}`}
                    </Tag>
                  </div>
                </TagGroup>
                <div className={styles.headerEditActions}>
                  <Button
                    type="submit"
                    appearance="link"
                    display="square"
                    extraClasses={`${styles.headerEditAction} ${styles.headerConfirmAction}`}
                    loading={isSaving}
                    disabled={isSaving}
                    aria-label="Confirm calculation header changes"
                  >
                    <Icon InlineIcon={IconCheckmark} aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    appearance="link"
                    display="square"
                    extraClasses={`${styles.headerEditAction} ${styles.headerCancelAction}`}
                    onClick={cancelEdit}
                    disabled={isSaving}
                    aria-label="Cancel calculation header changes"
                  >
                    <Icon InlineIcon={IconClose} aria-hidden="true" />
                  </Button>
                </div>
                <span className={styles.infoIcon} aria-hidden="true">
                  <Icon InlineIcon={IconInfoSolid} aria-hidden="true" />
                </span>
                <span className={styles.lastUpdated}>
                  Last edited: {formatLastEditedAt(updatedAt)}
                </span>
                {submitError && (
                  <div className={styles.submitError}>
                    <Infobox appearance="danger" small>
                      {submitError}
                    </Infobox>
                  </div>
                )}
              </form>
            ) : (
              <div className={styles.detailsGrid}>
                <span className={styles.calculationName} title={name}>
                  {name}
                </span>
                <span className={styles.kamLine} title={`KAM: ${kamName}`}>
                  KAM: {kamName}
                </span>
                <TagGroup>
                  <div className={styles.tags}>
                    <Tag display="outlined" small>
                      {line}
                    </Tag>
                    <Tag display="outlined" small>
                      {`${formatDate(startDate)} - ${formatDate(endDate)}`}
                    </Tag>
                  </div>
                </TagGroup>
                <Button
                  display="square"
                  appearance="link"
                  aria-label="Edit calculation header"
                  autoFocus={shouldRestoreEditFocus}
                  onClick={enterEdit}
                >
                  <Icon InlineIcon={IconEditSolid} aria-hidden="true" />
                </Button>
                <span className={styles.infoIcon} aria-hidden="true">
                  <Icon InlineIcon={IconInfoSolid} aria-hidden="true" />
                </span>
                <span className={styles.lastUpdated}>
                  Last edited: {formatLastEditedAt(updatedAt)}
                </span>
              </div>
            )}
          </div>
        </GridCell>
        <GridCell
          colSpan={{ default: 12, md: 4 }}
          justifySelf="end"
          extraClasses={styles.rightColumn}
        >
          <CalculationLifecycleControls
            status={status}
            hasIncompleteResults={hasIncompleteResults}
            activeSubcategoryHasCosts={activeSubcategoryHasCosts}
            incompleteReason={incompleteReason}
            inFlight={inFlight}
            onAddCost={onAddCost}
            onMarkComplete={onMarkComplete}
            onRecalculate={onRecalculate}
            onReopen={onReopen}
          />
        </GridCell>
      </Grid>
      <CalculationLifecycleErrors
        completeError={completeError}
        recalculateError={recalculateError}
      />
    </div>
  );
};

export default CalculationInfoBar;
